/**
 * **THE PLAYER'S SUIKA** — the one wallet every Suikamon of this player spends from and earns into
 * (one per player since 2026-09-28; the per-pet pockets are gone). Read from the trainer profile,
 * which is where the wallet lives, for signed-up players AND guests (a guest's wallet is keyed by
 * its session and moves into the account at sign-up).
 *
 * Every screen that shows or checks Suika reads it here, so none can do its own math again — the
 * player bar used to add the old pet pockets to the trainer's and the profile did not.
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWalletConnection } from '../auth/useWalletConnection';
import { fetchTrainerProfile } from '../api/petBackendApi';
import { trainerProfileQueryKey } from './useTrainerProfile';
import { GAME_CONSTANTS } from '@shared/domain/gameConstants';

/** Same freshness as the profile everywhere else — it is the same cache entry. */
const WALLET_STALE_TIME_MS = 30_000;

export interface PlayerSuika {
  /** The wallet's balance (0 while unknown or for a player with no wallet yet). */
  balance: number;
  /** True while the first read is in flight. */
  isLoading: boolean;
  /** Below the free-refill threshold: the one moment a free refill is offered. */
  isLow: boolean;
  /** Re-read the wallet (after a spend, a win or a refill). */
  refresh: () => void;
}

export function usePlayerSuika(): PlayerSuika {
  const { actingAddress } = useWalletConnection();
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: trainerProfileQueryKey(actingAddress),
    queryFn: () => fetchTrainerProfile(actingAddress as string),
    enabled: !!actingAddress,
    staleTime: WALLET_STALE_TIME_MS,
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: trainerProfileQueryKey(actingAddress) });
  }, [queryClient, actingAddress]);

  const balance = Math.floor(profile?.suikaBalance ?? 0);
  return { balance, isLoading, isLow: balance < GAME_CONSTANTS.SUIKA_LOW_THRESHOLD, refresh };
}
